import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import SignatureCanvas from "react-signature-canvas";

const SECCIONES_INSPECCION = [
  {
    nombre: "Tablero, Luces y Accesorios",
    items: [
      { key: "testigos_tablero", label: "Testigos del tablero (ninguno encendido)" },
      { key: "luces_frontales", label: "Luces frontales: bajas, medias, parqueo" },
      { key: "luces_traseras", label: "Luces traseras: direccionales, freno, reversa" },
      { key: "limpiaparabrisas", label: "Limpiaparabrisas (expulsa líquido y funciona)" },
      { key: "pito", label: "Pito delantero y de reversa (señal sonora)" },
      { key: "vidrios_espejos", label: "Vidrios y espejos (no fisurados)" },
      { key: "cerraduras", label: "Cerraduras (funcionan manualmente)" },
      { key: "cinturones", label: "Cinturones de seguridad (sin obstrucciones)" },
      { key: "silla_conductor", label: "Silla del conductor (graduable, cojinería sana)" },
      { key: "pedales", label: "Pedales (progresivos, sin obstrucciones)" },
      { key: "cojineria", label: "Cojinería general (no rota)" }
    ]
  },
  {
    nombre: "Motor y Niveles de Líquidos",
    items: [
      { key: "nivel_aceite", label: "Nivel de aceite" },
      { key: "nivel_refrigerante", label: "Nivel de líquido refrigerante" },
      { key: "nivel_direccion", label: "Nivel de líquido de dirección" },
      { key: "nivel_frenos", label: "Nivel de líquido de frenos" },
      { key: "correas", label: "Estado de correas (no flojas/deshilachadas)" },
      { key: "bateria", label: "Batería (bornes ajustados)" },
      { key: "fugas_motor", label: "Fugas (agua, aceite o ruidos extraños)" }
    ]
  },
  {
    nombre: "Dirección, Frenos y Llantas",
    items: [
      { key: "palanca_cambios", label: "Palanca de cambios (sin obstrucciones)" },
      { key: "sistema_direccion", label: "Sistema de dirección (sin ruidos extraños)" },
      { key: "freno_mano", label: "Freno de mano (vehículo no se mueve)" },
      { key: "fugas_frenos", label: "Fugas de líquido de frenos (manguetas, rines)" },
      { key: "rines", label: "Rines (sin fisuras, con espárragos completos)" },
      { key: "estado_llantas", label: "Estado de llantas (sin bultos o cortaduras)" },
      { key: "labrado_llantas", label: "Labrado (encima de los triángulos)" },
      { key: "presion_llantas", label: "Presión de inflado (pareja y adecuada)" }
    ]
  },
  {
    nombre: "Equipo de Carretera y Estado General",
    items: [
      { key: "botiquin", label: "Botiquín de primeros auxilios (vigente/dotación)" },
      { key: "conos", label: "Conos y/o triángulos" },
      { key: "caja_herramientas", label: "Caja de herramientas (alicate, llaves, gato)" },
      { key: "linterna", label: "Linterna" },
      { key: "golpes_rayones", label: "Golpes o rayones no reportados" },
      { key: "aseo", label: "Aseo del vehículo" }
    ]
  }
];

export default function Preoperacional({ usuario, onBackToMenu }) {
  const API_URL = process.env.REACT_APP_API_URL;

  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === "string"
        ? JSON.parse(usuario.permisos_avanzados)
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed;
    } catch (e) {
      return {};
    }
  }, [usuario]);

  const cargoUpper = String(usuario?.cargo || "").toUpperCase().trim();
  const esAdminGlobal = cargoUpper === "SUPER_ADMIN" || cargoUpper === "ADMIN" || Object.keys(permisosABAC).length === 0;
  
  const checkPermiso = (nombreSubmodulo) => {
    if (esAdminGlobal) return true;
    return permisosABAC[nombreSubmodulo] !== undefined;
  };

  // Validación real con ABAC para Historial y Flota (Nueva Inspección se maneja desde la tarjeta del vehículo)
  const tienePermisoHistorial = checkPermiso("Historial Preoperacional") || esAdminGlobal;
  const tienePermisoFlota = checkPermiso("Nueva Inspección") || esAdminGlobal;

  const inspectorNombre = `${usuario?.nombres || ""} ${usuario?.apellidos || ""}`.trim() || usuario?.nombre || "Conductor";

  const [vistaActual, setVistaActual] = useState("flota");
  const sigCanvas = useRef(null);

  const [registros, setRegistros] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [detalleActivo, setDetalleActivo] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const [vehiculosDisponibles, setVehiculosDisponibles] = useState([]);
  const [vehiculoSeleccionadoFicha, setVehiculoSeleccionadoFicha] = useState(null);

  const [formData, setFormData] = useState({
    vehiculo: "",
    fecha: new Date().toISOString().split("T")[0],
    hora: new Date().toTimeString().split(" ")[0].substring(0, 5),
    turno: "Mañana",
    serial: "",
    horometro: "",
    ubicacion: usuario?.centroTrabajo || "Sede Principal",
    clima: "Seco",
    condicion_inicial: "",
    estado_autorizacion: "Operativo",
    conclusion: "",
    licencia: "Vigente",
    soat: "Vigente",
    tecnomecanica: "Vigente",
    seguro_riesgo: "Vigente"
  });

  const [respuestas, setRespuestas] = useState({});
  const [evidencias, setEvidencias] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState({ text: "", type: "" });

  const inputClass = "w-full h-[48px] px-5 bg-white/60 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-inner transition-all appearance-none";

  const cargarVehiculosPorSede = useCallback(async () => {
    try {
      let res = await fetch(`${API_URL}/api/admin/vehiculos`, {
        headers: {
          "x-user-data": JSON.stringify(usuario || {}),
          "x-abac-permissions": JSON.stringify(permisosABAC || {})
        }
      });
      if (!res.ok) {
        res = await fetch(`${API_URL}/api/preoperacional/vehiculos/flota`);
      }
      const data = await res.json();
      if (data && data.success && Array.isArray(data.data)) {
        setVehiculosDisponibles(data.data);
      }
    } catch (error) {
      console.error("Error al cargar vehículos:", error);
    }
  }, [API_URL, permisosABAC, usuario]);

  const cargarRegistros = useCallback(async () => {
    setCargandoLista(true);
    try {
      const res = await fetch(`${API_URL}/api/preoperacional`, {
        headers: {
          "x-user-data": JSON.stringify(usuario || {}),
          "x-abac-permissions": JSON.stringify(permisosABAC || {})
        }
      });
      const data = await res.json();
      if (data && data.success) {
        setRegistros(data.registros || []);
      }
    } catch (error) {
      console.error("Error al cargar historial:", error);
    } finally {
      setCargandoLista(false);
    }
  }, [API_URL, usuario, permisosABAC]);

  useEffect(() => {
    cargarVehiculosPorSede();
    cargarRegistros();
  }, [cargarVehiculosPorSede, cargarRegistros]);

  const iniciarInspeccionParaVehiculo = (vehiculo) => {
    setVehiculoSeleccionadoFicha(vehiculo);
    const respuestasIniciales = {};
    SECCIONES_INSPECCION.forEach((sec) => {
      sec.items.forEach((item) => {
        respuestasIniciales[item.key] = { seccion: sec.nombre, valor: "Bueno", observacion: "" };
      });
    });
    setRespuestas(respuestasIniciales);
    setEvidencias({});
    setFormData((prev) => ({
      ...prev,
      vehiculo: vehiculo.placa,
      horometro: vehiculo.kilometraje_actual || ""
    }));
    setVistaActual("formulario");
  };

  const cargarDetalle = async (id) => {
    setCargandoDetalle(true);
    setVistaActual("detalle");
    try {
      const res = await fetch(`${API_URL}/api/preoperacional/${id}`, {
        headers: {
          "x-user-data": JSON.stringify(usuario || {}),
          "x-abac-permissions": JSON.stringify(permisosABAC || {})
        }
      });
      const data = await res.json();
      if (data && data.success) {
        setDetalleActivo(data);
      }
    } catch (error) {
      setVistaActual("historial");
    } finally {
      setCargandoDetalle(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRespuestaChange = (seccion, preguntaKey, campo, valor) => {
    setRespuestas((prev) => ({
      ...prev,
      [preguntaKey]: { ...prev[preguntaKey], seccion, [campo]: valor }
    }));
  };

  const handleEvidenciaChange = (seccion, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setEvidencias((prev) => ({ ...prev, [seccion]: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehiculo || !formData.horometro || !formData.turno) {
      return setMensaje({ text: "⚠️ Placa, Kilometraje y Turno son obligatorios.", type: "error" });
    }

    if (sigCanvas.current && sigCanvas.current.isEmpty()) {
      return setMensaje({ text: "⚠️ Por favor traza tu firma digital antes de guardar.", type: "error" });
    }

    setEnviando(true);
    setMensaje({ text: "", type: "" });
    const notasLegales = `Lic: ${formData.licencia} | SOAT: ${formData.soat} | Tecno: ${formData.tecnomecanica} | Seguro: ${formData.seguro_riesgo}`;
    const condicionFinal = `${notasLegales} - Nota: ${formData.condicion_inicial || "Sin novedades"}`;
    const firmaBase64 = sigCanvas.current ? sigCanvas.current.getCanvas().toDataURL("image/png") : "";

    try {
      const payload = {
        registro: {
          ...formData,
          vehiculo: String(formData.vehiculo).toUpperCase().trim(),
          inspector: inspectorNombre,
          condicion_inicial: condicionFinal,
          firma_operador: firmaBase64
        },
        respuestas,
        evidencias
      };
      const response = await fetch(`${API_URL}/api/preoperacional`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-data": JSON.stringify(usuario || {}),
          "x-abac-permissions": JSON.stringify(permisosABAC || {})
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data && data.success) {
        setMensaje({ text: "✅ ¡Inspección preoperacional guardada con éxito!", type: "success" });
        await cargarRegistros();
        await cargarVehiculosPorSede();
        setTimeout(() => {
          setVistaActual("historial");
          setMensaje({ text: "", type: "" });
        }, 1200);
      } else {
        setMensaje({ text: data.message || "Error al guardar la inspección.", type: "error" });
      }
    } catch (error) {
      setMensaje({ text: "Error de conexión con el servidor.", type: "error" });
    } finally {
      setEnviando(false);
    }
  };

  const calcularEstadoVigencia = (fechaStr) => {
    if (!fechaStr) return { texto: "VIGENTE", clase: "bg-emerald-100 text-emerald-700" };
    const diferenciaDias = Math.ceil((new Date(fechaStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (diferenciaDias < 0) return { texto: "VENCIDO", clase: "bg-red-100 text-red-700" };
    if (diferenciaDias <= 30) return { texto: `VENCE EN ${diferenciaDias} DÍAS`, clase: "bg-amber-100 text-amber-700" };
    return { texto: "VIGENTE", clase: "bg-emerald-100 text-emerald-700" };
  };

  const registrosPorVehiculo = useMemo(() => {
    return registros.reduce((acc, reg) => {
      const placa = String(reg.vehiculo || "DESCONOCIDO").toUpperCase().trim();
      if (!acc[placa]) acc[placa] = [];
      acc[placa].push(reg);
      return acc;
    }, {});
  }, [registros]);

  const renderHeader = (titulo, subtitulo) => (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-200/50 pb-6 mb-8 gap-4">
      <div className="flex items-center gap-5">
        <div className="w-14 h-14 rounded-2xl bg-blue-50/80 border border-blue-100 flex items-center justify-center text-3xl shadow-inner text-blue-600">🚛</div>
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">{titulo}</h2>
          <p className="text-xs sm:text-sm text-slate-500 font-bold mt-1">{subtitulo}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 w-full sm:w-auto">
        {tienePermisoFlota && (
          <button
            onClick={() => setVistaActual("flota")}
            className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer ${
              vistaActual === "flota" ? "bg-blue-600 text-white shadow-lg" : "bg-white/70 text-slate-700 hover:bg-white border border-slate-200"
            }`}
          >
            🚛 Flota Activa
          </button>
        )}
        {tienePermisoHistorial && (
          <button
            onClick={() => setVistaActual("historial")}
            className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer ${
              vistaActual === "historial" ? "bg-blue-600 text-white shadow-lg" : "bg-white/70 text-slate-700 hover:bg-white border border-slate-200"
            }`}
          >
            📋 Historial ({registros.length})
          </button>
        )}
        <button
          onClick={onBackToMenu}
          className="px-5 py-2.5 rounded-2xl text-xs font-bold text-red-600 bg-red-50/50 hover:bg-red-100 transition-all cursor-pointer border border-red-100"
        >
          ✕ Cerrar
        </button>
      </div>
    </div>
  );

  if (vistaActual === "flota") {
    return (
      <div className="w-full min-h-screen py-6 px-4 flex flex-col items-center relative z-10">
        <div className="bg-white/40 backdrop-blur-[45px] rounded-[36px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-6 sm:p-10 max-w-7xl w-full mx-auto h-[90vh] flex flex-col border border-white/80 overflow-hidden relative animate-fade-in">
          {renderHeader("Flota de Vehículos", "Seleccione un vehículo para realizar la inspección preoperacional.")}
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
            {vehiculosDisponibles.length === 0 ? (
              <div className="text-center p-12 bg-white/60 rounded-[32px] border border-white/80 text-slate-800">
                <span className="text-5xl block mb-4 opacity-50">🚛</span>
                <p className="font-black text-base text-slate-700 mb-4">No hay vehículos registrados en la flota.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {vehiculosDisponibles.map((v) => {
                  const soatEstado = calcularEstadoVigencia(v.soat_vencimiento);
                  const tecnoEstado = calcularEstadoVigencia(v.tecno_vencimiento);
                  return (
                    <div key={v.id || v.placa} className="bg-white/70 backdrop-blur-xl border border-white rounded-[28px] p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                      <div>
                        <div className="flex justify-between items-start border-b border-slate-200/50 pb-4 mb-4">
                          <div>
                            <h3 className="font-black text-slate-800 uppercase tracking-widest text-xl">{v.placa}</h3>
                            <span className="text-[9px] font-black text-blue-700 bg-blue-100/50 px-2.5 py-1 rounded-lg uppercase tracking-wider mt-2 inline-block">
                              {v.clase_vehiculo || "CAMIÓN"} / {v.tipo || "CARGA"}
                            </span>
                          </div>
                          <span className="text-2xl">🚛</span>
                        </div>
                        <div className="space-y-2 text-xs text-slate-700 mb-6 font-bold">
                          <p><strong className="text-slate-400 font-extrabold uppercase text-[10px]">Marca:</strong> {v.marca || "FLOTA EGS"}</p>
                          <p><strong className="text-slate-400 font-extrabold uppercase text-[10px]">Sede:</strong> {v.ciudad || "FUSAGASUGÁ"}</p>
                          <p><strong className="text-slate-400 font-extrabold uppercase text-[10px]">Último KM/Horómetro:</strong> <span className="text-blue-700">{v.kilometraje_actual || 0} km</span></p>
                          <div className="pt-4 flex gap-2 flex-wrap border-t border-slate-100">
                            <span className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider ${soatEstado.clase}`}>SOAT: {soatEstado.texto}</span>
                            <span className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider ${tecnoEstado.clase}`}>TECNO: {tecnoEstado.texto}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => iniciarInspeccionParaVehiculo(v)}
                        className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-500/30 cursor-pointer"
                      >
                        📝 Hacer Inspección
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (vistaActual === "historial") {
    return (
      <div className="w-full min-h-screen py-6 px-4 flex flex-col items-center relative z-10">
        <div className="bg-white/40 backdrop-blur-[45px] rounded-[36px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-6 sm:p-10 max-w-7xl w-full mx-auto h-[90vh] flex flex-col border border-white/80 overflow-hidden relative animate-fade-in">
          {renderHeader("Historial Preoperacional", "Auditoría de inspecciones agrupadas por placa.")}
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
            {cargandoLista ? (
              <div className="flex justify-center items-center h-40">
                <span className="text-slate-500 font-black text-sm uppercase tracking-widest animate-pulse">Cargando historial... 🚛</span>
              </div>
            ) : Object.keys(registrosPorVehiculo).length === 0 ? (
              <div className="text-center p-12 bg-white/60 rounded-[32px] border border-white/80 text-slate-800">
                <span className="text-5xl block mb-4 opacity-50">📭</span>
                <p className="font-black text-base text-slate-600">No hay inspecciones registradas.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(registrosPorVehiculo).map(([placa, inspecciones]) => (
                  <div key={placa} className="bg-white/70 backdrop-blur-xl border border-white rounded-[28px] p-6 flex flex-col shadow-sm">
                    <div className="flex items-center justify-between border-b border-slate-200/50 pb-5 mb-5">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-white/80 rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white">🚐</div>
                        <div>
                          <h3 className="font-black text-slate-800 uppercase tracking-widest text-xl">{placa}</h3>
                          <span className="text-[9px] font-black text-blue-700 bg-blue-100/50 px-2.5 py-1 rounded-lg mt-2 inline-block uppercase tracking-wider">{inspecciones.length} Registros</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 space-y-3 overflow-y-auto max-h-[260px] pr-2 custom-scrollbar">
                      {inspecciones.map((reg) => {
                        const estAut = String(reg.estado_autorizacion || "Operativo");
                        const fechaTexto = reg.fecha ? new Date(reg.fecha).toLocaleDateString() : "";
                        return (
                          <div key={reg.id} className="bg-white/80 p-4 rounded-2xl flex justify-between items-center border border-white shadow-sm hover:border-blue-300 transition-all">
                            <div className="flex-1 min-w-0 pr-3">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="text-[9px] font-black text-slate-800 bg-slate-100 px-2 py-1 rounded-md uppercase">#{reg.id}</span>
                                <span className={`w-2.5 h-2.5 rounded-full shadow-sm ${estAut.includes("Operativo") ? "bg-emerald-500" : "bg-red-500"}`}></span>
                                <span className="text-[9px] font-black text-amber-800 bg-amber-100 px-2 py-1 rounded-md">{reg.horometro || 0} KM</span>
                              </div>
                              <p className="text-xs font-bold text-slate-900 truncate">{fechaTexto} - {reg.hora}</p>
                              <p className="text-[10px] text-slate-500 font-extrabold truncate mt-1 uppercase">{reg.inspector}</p>
                            </div>
                            <button
                              onClick={() => cargarDetalle(reg.id)}
                              className="px-4 py-2.5 bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white rounded-xl text-[10px] font-black transition-colors cursor-pointer border border-blue-100 shadow-sm"
                            >
                              Ver
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (vistaActual === "detalle") {
    if (cargandoDetalle || !detalleActivo) {
      return <div className="bg-white/60 p-12 rounded-[34px] text-center font-black animate-pulse mt-10 max-w-md mx-auto">Cargando reporte...</div>;
    }
    const { registro, respuestas: resBD = {}, evidencias: eviBD = {} } = detalleActivo;
    const estAut = String(registro.estado_autorizacion || "Operativo");
    return (
      <div className="w-full min-h-screen py-6 px-4 flex flex-col items-center relative z-10">
        <div className="bg-white/60 backdrop-blur-[45px] rounded-[36px] shadow-[0_25px_70px_rgba(0,0,0,0.15)] p-6 sm:p-10 max-w-5xl w-full mx-auto max-h-[92vh] overflow-y-auto border border-white/80 animate-fade-in custom-scrollbar text-slate-900">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-200 pb-6 mb-8 sticky top-0 bg-white/80 backdrop-blur-xl z-20 pt-4 rounded-b-[24px] px-4 sm:-mx-6 sm:px-6 shadow-sm">
            <div>
              <h2 className="text-2xl font-black tracking-tight text-slate-800">Reporte #{registro.id}</h2>
              <p className="text-xs text-slate-500 font-bold mt-1">Placa: <span className="text-slate-900 uppercase">{registro.vehiculo}</span></p>
            </div>
            <button onClick={() => setVistaActual("historial")} className="px-6 py-3 rounded-2xl bg-white hover:bg-slate-50 text-slate-800 text-xs font-black transition-all cursor-pointer border border-slate-200 shadow-sm">
              Volver al Historial
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-8 bg-white/70 backdrop-blur-xl p-6 rounded-[28px] border border-white shadow-sm">
            <div><p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Inspector</p><p className="text-sm font-bold mt-1 text-slate-800">{registro.inspector}</p></div>
            <div><p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Fecha y Hora</p><p className="text-sm font-bold mt-1 text-slate-800">{registro.fecha ? new Date(registro.fecha).toLocaleDateString() : ""} {registro.hora}</p></div>
            <div><p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Turno / Ubicación</p><p className="text-sm font-bold mt-1 text-slate-800">{registro.turno || "N/A"} / {registro.ubicacion || "Sede"}</p></div>
            <div><p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">KM Registrado</p><p className="text-sm font-black mt-1 text-blue-700">{registro.horometro} km</p></div>
            <div className="col-span-2 sm:col-span-4 border-t border-slate-200 pt-4 mt-2">
              <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Notas Legales y Condición Inicial</p>
              <p className="text-xs text-slate-700 font-bold mt-1.5">{registro.condicion_inicial}</p>
            </div>
          </div>
          {SECCIONES_INSPECCION.map((seccion, idx) => (
            <div key={idx} className="mb-6 bg-white/70 backdrop-blur-xl border border-white rounded-[28px] overflow-hidden shadow-sm">
              <div className="bg-white/80 p-5 border-b border-slate-200"><h3 className="font-black text-sm uppercase tracking-wider text-slate-800">{seccion.nombre}</h3></div>
              <div className="p-6 flex flex-col md:flex-row gap-8">
                <div className="flex-1 space-y-4">
                  {seccion.items.map((item) => {
                    const r = resBD[item.key] || { valor: "N/A", observacion: "" };
                    const color = r.valor === "Bueno" ? "bg-emerald-100 text-emerald-800" : r.valor === "Malo" ? "bg-red-500 text-white" : r.valor === "Regular" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-600";
                    return (
                      <div key={item.key} className="flex justify-between items-start border-b border-slate-100 pb-3">
                        <div className="flex-1 pr-4">
                          <p className="text-xs font-bold text-slate-800">{item.label}</p>
                          {r.observacion && <p className="text-[10px] text-red-700 mt-2 bg-red-50 p-2 rounded-xl inline-block font-black border border-red-100">Obs: {r.observacion}</p>}
                        </div>
                        <span className={`text-[10px] font-black px-3 py-1.5 rounded-lg uppercase tracking-widest ${color}`}>{r.valor}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="w-full md:w-1/3 flex flex-col items-center justify-center bg-white/50 rounded-[24px] p-6 border-2 border-dashed border-slate-200">
                  <p className="text-[10px] text-slate-400 font-black mb-3 uppercase tracking-widest">Evidencia Fotográfica</p>
                  {eviBD[seccion.nombre] ? (
                    <img src={eviBD[seccion.nombre]} alt="Evidencia" className="rounded-2xl max-h-40 object-cover shadow-sm cursor-zoom-in hover:scale-105 transition" onClick={() => window.open(eviBD[seccion.nombre], "_blank")} />
                  ) : (
                    <div className="h-32 flex items-center justify-center text-slate-400 text-xs font-bold">Sin foto</div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div className="bg-white/70 backdrop-blur-xl p-8 rounded-[32px] border border-white shadow-sm mt-8 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex-1 w-full">
              <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Novedades / Conclusión</p>
              <p className="text-sm font-bold text-slate-900 mt-2">{registro.conclusion || "Ninguna novedad reportada."}</p>
            </div>
            <div className="flex flex-col items-center bg-white p-6 rounded-[24px] border border-slate-100 shadow-sm w-full md:w-auto">
              <p className="text-[10px] text-slate-400 uppercase font-black mb-3 tracking-widest">Firma Digital</p>
              {registro.firma_operador && registro.firma_operador.startsWith("data:image") ? (
                <img src={registro.firma_operador} alt="Firma" className="h-20 object-contain pointer-events-none" />
              ) : (
                <p className="text-sm font-mono text-slate-500 italic h-20 flex items-center">{registro.firma_operador || "Sin firma"}</p>
              )}
              <p className="text-xs font-black text-slate-900 mt-4 pt-3 border-t border-slate-100 w-full text-center">{registro.inspector}</p>
            </div>
            <span className={`px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest shadow-md ${estAut.includes("Operativo") ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`}>
              {estAut}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (vistaActual === "formulario") {
    return (
      <div className="w-full min-h-screen py-6 px-4 flex flex-col items-center animate-fade-in relative z-10">
        <div className="bg-white/50 backdrop-blur-[45px] rounded-[36px] shadow-[0_25px_70px_rgba(0,0,0,0.15)] p-6 sm:p-10 max-w-6xl w-full mx-auto h-[90vh] overflow-y-auto border border-white/85 relative z-10 custom-scrollbar text-slate-900">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-200 pb-6 mb-8 gap-4 sticky top-0 bg-white/80 backdrop-blur-xl z-30 px-6 sm:-mx-10 sm:px-10 pt-4 rounded-b-[24px] shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-2xl shadow-inner">📝</div>
              <div>
                <h2 className="text-2xl font-black tracking-tight text-slate-900">INSPECCIÓN DIARIA</h2>
                <p className="text-xs font-bold text-slate-500 mt-1">FT-SST-27 | Placa: <span className="text-blue-600">{formData.vehiculo || "Ingresar Placa"}</span></p>
              </div>
            </div>
            <button onClick={() => setVistaActual("flota")} type="button" className="px-6 py-3 rounded-2xl bg-white hover:bg-slate-50 text-slate-800 text-xs font-bold transition-all border border-slate-200 shadow-sm cursor-pointer">
              Cancelar y Volver
            </button>
          </div>

          {mensaje.text && (
            <div className={`mb-8 p-5 rounded-2xl text-xs font-black border ${mensaje.type === "success" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
              {mensaje.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {vehiculoSeleccionadoFicha && (
              <div className="bg-blue-50/50 backdrop-blur-xl p-6 sm:p-8 rounded-[32px] border border-blue-100 shadow-sm">
                <h3 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-4 border-b border-blue-200 pb-3 flex items-center gap-2">📋 Ficha Técnica</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-5 text-xs font-bold">
                  <div><span className="text-slate-400 block text-[10px] uppercase mb-1">Ciudad</span><strong className="text-slate-800">{vehiculoSeleccionadoFicha.ciudad || "FUSAGASUGÁ"}</strong></div>
                  <div><span className="text-slate-400 block text-[10px] uppercase mb-1">Clase / Tipo</span><strong className="text-slate-800">{vehiculoSeleccionadoFicha.clase_vehiculo || "CAMIÓN"} / {vehiculoSeleccionadoFicha.tipo || "CARGA"}</strong></div>
                  <div><span className="text-slate-400 block text-[10px] uppercase mb-1">Marca / Línea</span><strong className="text-slate-800">{vehiculoSeleccionadoFicha.marca || "N/A"} - {vehiculoSeleccionadoFicha.linea || "N/A"}</strong></div>
                  <div><span className="text-slate-400 block text-[10px] uppercase mb-1">Km Base</span><strong className="text-blue-700 text-sm">{vehiculoSeleccionadoFicha.kilometraje_actual || 0} km</strong></div>
                </div>
              </div>
            )}

            <div className="bg-white/70 backdrop-blur-xl p-8 rounded-[32px] border border-white shadow-sm grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
              {!vehiculoSeleccionadoFicha && (
                <div className="w-full flex flex-col">
                  <label className="block text-[10px] uppercase font-black mb-2 text-blue-700">Placa del Vehículo *</label>
                  <input
                    type="text"
                    name="vehiculo"
                    placeholder="Ej: JPP406"
                    value={formData.vehiculo}
                    onChange={handleInputChange}
                    className={`${inputClass} uppercase`}
                    required
                  />
                </div>
              )}
              <div className="w-full flex flex-col relative">
                <label className="block text-[10px] uppercase font-black mb-2 text-blue-700">KM / Horómetro Nuevo *</label>
                <input
                  type="number"
                  name="horometro"
                  value={formData.horometro}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="Ej: 150200"
                  required
                />
              </div>
              <div className="w-full flex flex-col relative">
                <label className="block text-[10px] uppercase font-black mb-2 text-slate-500">Turno *</label>
                <select name="turno" value={formData.turno} onChange={handleInputChange} className={`${inputClass} pr-10 cursor-pointer`} required>
                  <option value="" disabled>Seleccionar...</option>
                  <option value="Mañana">Mañana</option>
                  <option value="Tarde">Tarde</option>
                  <option value="Noche">Noche</option>
                </select>
              </div>
              <div className="w-full flex flex-col">
                <label className="block text-[10px] uppercase font-black mb-2 text-slate-500">Fecha *</label>
                <input type="date" name="fecha" value={formData.fecha} onChange={handleInputChange} className={inputClass} required />
              </div>
              <div className="w-full flex flex-col">
                <label className="block text-[10px] uppercase font-black mb-2 text-slate-500">Hora *</label>
                <input type="time" name="hora" value={formData.hora} onChange={handleInputChange} className={inputClass} required />
              </div>
              <div className="md:col-span-4 h-px bg-slate-200 my-2"></div>
              {["licencia", "soat", "tecnomecanica", "seguro_riesgo"].map((doc) => (
                <div key={doc} className="w-full flex flex-col relative">
                  <label className="block text-[10px] uppercase font-black mb-2 text-slate-500">Doc. {doc.replace("_", " ")} *</label>
                  <select name={doc} value={formData[doc]} onChange={handleInputChange} className={`${inputClass} pr-10 cursor-pointer`} required>
                    <option value="" disabled>Seleccionar...</option>
                    <option value="Vigente">Físico Vigente</option>
                    <option value="No vigente">No lo porto / Vencido</option>
                    {doc === "seguro_riesgo" && <option value="No aplica">No aplica</option>}
                  </select>
                </div>
              ))}
            </div>

            {SECCIONES_INSPECCION.map((seccion, idx) => (
              <div key={idx} className="bg-white/70 backdrop-blur-xl border border-white rounded-[32px] p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 border-b border-slate-200 pb-4 gap-4">
                  <h3 className="font-black text-sm uppercase tracking-widest text-slate-800">{seccion.nombre}</h3>
                  <label className="text-xs font-black text-blue-700 bg-white hover:bg-slate-50 px-4 py-2.5 rounded-xl cursor-pointer transition border border-slate-200 shadow-sm flex items-center gap-2">
                    <span>📸 {evidencias[seccion.nombre] ? "Foto Cargada ✅" : "Adjuntar Foto"}</span>
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleEvidenciaChange(seccion.nombre, e)} />
                  </label>
                </div>
                <div className="space-y-4">
                  {seccion.items.map((item) => (
                    <div key={item.key} className="flex flex-col lg:flex-row lg:items-center gap-4 bg-white/80 p-5 rounded-[24px] border border-slate-100 shadow-sm">
                      <span className="text-xs font-black w-full lg:w-1/3 text-slate-800">{item.label}</span>
                      <div className="grid grid-cols-4 sm:flex sm:flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                        {[
                          { key: "Bueno", color: "bg-emerald-600 text-white" },
                          { key: "Regular", color: "bg-amber-500 text-white" },
                          { key: "Malo", color: "bg-red-600 text-white" },
                          { key: "N.A", color: "bg-slate-600 text-white" }
                        ].map((opcion) => (
                          <button
                            key={opcion.key}
                            type="button"
                            onClick={() => handleRespuestaChange(seccion.nombre, item.key, "valor", opcion.key)}
                            className={`px-2 py-2.5 sm:px-4 sm:py-2 flex items-center justify-center text-[10px] font-black rounded-xl transition cursor-pointer ${
                              respuestas[item.key]?.valor === opcion.key ? `${opcion.color} shadow-lg scale-105 z-10` : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                            }`}
                          >
                            {opcion.key}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        placeholder="Observación..."
                        className="w-full lg:flex-1 h-[48px] px-5 border border-slate-200 rounded-2xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 font-bold shadow-inner"
                        value={respuestas[item.key]?.observacion || ""}
                        onChange={(e) => handleRespuestaChange(seccion.nombre, item.key, "observacion", e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="bg-white/70 backdrop-blur-xl p-6 sm:p-8 rounded-[32px] border border-white grid grid-cols-1 sm:grid-cols-2 gap-8 shadow-sm">
              <div className="sm:col-span-2">
                <label className="block text-xs font-black mb-3 uppercase tracking-widest text-slate-800">Estado de Autorización *</label>
                <div className="flex flex-col sm:flex-row gap-4 mt-2">
                  {["Operativo", "Operativo con Restricción", "Inoperativo"].map((estado) => (
                    <label
                      key={estado}
                      className={`flex items-center gap-3 text-xs font-black cursor-pointer px-5 py-4 rounded-2xl border transition-all shadow-sm ${
                        formData.estado_autorizacion === estado ? "bg-blue-600 text-white border-blue-700 shadow-blue-500/30" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="estado_autorizacion"
                        value={estado}
                        onChange={handleInputChange}
                        checked={formData.estado_autorizacion === estado}
                        className="w-4 h-4 cursor-pointer hidden"
                        required
                      />
                      {estado}
                    </label>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-black mb-3 uppercase tracking-widest text-slate-500">Novedades / Conclusión</label>
                <textarea
                  name="conclusion"
                  value={formData.conclusion}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full p-5 border border-slate-200 rounded-[24px] text-sm bg-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 font-bold shadow-inner resize-none"
                  placeholder="Reporte cualquier novedad hallada..."
                ></textarea>
              </div>
              <div className="sm:col-span-2 bg-white/80 p-6 sm:p-8 rounded-[28px] border border-slate-100 shadow-sm">
                <label className="block text-[10px] font-black mb-4 uppercase tracking-widest text-slate-500">
                  Firma Digital * <span className="font-bold text-slate-400 normal-case">(traza tu firma aquí)</span>
                </label>
                <div className="border-2 border-dashed border-blue-200 rounded-[24px] bg-white overflow-hidden relative shadow-inner">
                  <SignatureCanvas ref={sigCanvas} penColor="black" canvasProps={{ className: "w-full h-40 sm:h-52 cursor-crosshair touch-none" }} />
                  <button
                    type="button"
                    onClick={() => sigCanvas.current && sigCanvas.current.clear()}
                    className="absolute top-4 right-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-[10px] font-black transition cursor-pointer shadow-sm"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end pt-4">
              <button
                type="submit"
                disabled={enviando}
                className={`w-full sm:w-auto px-12 py-5 rounded-2xl text-xs font-black text-white shadow-[0_4px_14px_rgba(0,122,255,0.3)] transition-transform uppercase tracking-widest ${
                  enviando ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5 cursor-pointer"
                }`}
              >
                {enviando ? "Procesando..." : "Guardar Inspección"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
}